import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import type { ReturnDto } from '@ecom/contracts';
import { useReturnsStore } from '@/stores/returns.store';
import { useUiStore } from '@/i18n';
import { useT } from '@/i18n/use-t';
import { count, dateTime, money } from '@/lib/format';
import { mediaUrl } from '@/lib/media';
import {
  dispositionKey,
  refundPayment,
  rejectionReasonKey,
  resolutionKey,
  returnReasonKey,
  returnStatusKey,
  returnStatusTone,
  returnSteps,
  type ReturnStep,
} from '@/lib/returns';
import { Sheet, Thumb } from '@/components/page';
import { Alert, Button, CopyButton, Pill, Skeleton } from '@/components/ui';

/**
 * One return, whole: what came back and why, what the store did about it, the
 * money, and the order it belongs to — with the steps it can take from here in
 * the footer, the usual next one last and strongest.
 *
 * The order is read beside it, best effort: it adds the picture, how many were
 * bought, how it was paid and who the customer is. Without it — staff who may
 * not read orders — the return still reads in full.
 */
export function ReturnSheet({
  open,
  isOwner,
  mayReadOrders,
  onClose,
  onStep,
}: {
  open: boolean;
  isOwner: boolean;
  /** False: the order is not read, and the panel says where its payment would be. */
  mayReadOrders: boolean;
  onClose: () => void;
  onStep: (step: ReturnStep, row: ReturnDto) => void;
}) {
  const { current, currentError, order, providerNames } = useReturnsStore();
  const locale = useUiStore((state) => state.locale);
  const t = useT();

  const ret = current;
  const linked = ret && order?.id === ret.orderId ? order : null;
  const line = linked?.items.find((item) => item.id === ret?.orderItemId) ?? null;
  const payment = refundPayment(linked?.payments);
  const steps = ret ? returnSteps(ret.status, isOwner) : [];
  // The usual next step is the strong one — and only if it is this person's to take.
  const usual = ret ? returnSteps(ret.status, true)[0] : undefined;
  // The owner alone refunds; staff see why there is no button rather than wondering.
  const refundWaitsOnOwner = ret?.status === 'RECEIVED' && !isOwner;

  return (
    <Sheet
      open={open}
      size="lg"
      title={ret?.number ?? t('returns')}
      subtitle={ret?.productName}
      onClose={onClose}
      footer={
        <>
          {refundWaitsOnOwner ? <span className="me-auto text-xs text-ink-muted">{t('ownerOnlyRefund')}</span> : null}
          <Button variant="secondary" onClick={onClose}>
            {t('close')}
          </Button>
          {ret
            ? // Secondary steps first, the usual next step last — the house order for actions.
              [...steps].reverse().map((step) => (
                <Button
                  key={step}
                  variant={step === usual ? 'primary' : step === 'reject' ? 'ghost' : 'secondary'}
                  onClick={() => onStep(step, ret)}
                >
                  {stepLabel(step, ret, t)}
                </Button>
              ))
            : null}
        </>
      }
    >
      {!ret ? (
        currentError ? (
          <Alert>{currentError}</Alert>
        ) : (
          <div className="flex flex-col gap-4">
            <Skeleton className="h-16" />
            <Skeleton className="h-40" />
          </div>
        )
      ) : (
        <div className="flex flex-col gap-6">
          <p className="flex flex-wrap items-center gap-2 text-xs text-ink-muted">
            <Pill tone={returnStatusTone(ret.status)}>{t(returnStatusKey(ret.status))}</Pill>
            <span>
              {t('returnRequestedOn')} {dateTime(ret.requestedAt, locale)}
            </span>
          </p>

          {currentError ? <Alert>{currentError}</Alert> : null}

          {/* What came back, as it was bought. */}
          <section>
            <h3 className="mb-2 text-sm font-semibold text-ink">{t('returnItemSection')}</h3>
            <div className="flex items-start gap-3 rounded-control border border-line p-3">
              <Thumb src={mediaUrl(line?.imageUrl)} size="sm" />
              <div className="min-w-0 flex-1">
                <p dir="auto" className="text-start text-sm font-medium text-ink">{ret.productName}</p>
                <p className="mt-0.5 text-xs text-ink-muted">
                  <span dir="ltr" data-numeric>
                    {ret.sku}
                  </span>
                  {line && Object.keys(line.optionLabels).length > 0
                    ? ` · ${Object.entries(line.optionLabels)
                        .map(([name, value]) => `${name}: ${value}`)
                        .join(locale === 'ar' ? '، ' : ', ')}`
                    : ''}
                </p>
              </div>
              <div className="shrink-0 text-end">
                <p data-numeric className="text-sm text-ink-muted">
                  {line
                    ? t('returnQtyOf', { count: count(ret.quantity, locale), ordered: count(line.quantity, locale) })
                    : t('returnQty', { count: count(ret.quantity, locale) })}
                </p>
                <p data-numeric className="text-sm font-medium text-ink">
                  {money(ret.amount, locale)}
                </p>
              </div>
            </div>
          </section>

          <div className="grid gap-6 sm:grid-cols-2">
            <Facts title={t('returnWhySection')}>
              <Fact label={t('returnReason')}>{t(returnReasonKey(ret.reason))}</Fact>
              <Fact label={t('returnResolutionLabel')}>{t(resolutionKey(ret.resolution))}</Fact>
            </Facts>

            <Facts title={t('returnHandlingSection')}>
              {ret.disposition ? (
                <Fact label={t('returnDisposition')}>
                  {t(dispositionKey(ret.disposition))}
                  <span className="block text-xs text-ink-muted">
                    {ret.restock
                      ? t('returnRestockedUnits', { count: count(ret.quantity, locale) })
                      : t('returnNotRestocked')}
                  </span>
                </Fact>
              ) : null}
              {ret.rejectionReason ? (
                <Fact label={t('rejectReasonLabel')}>{t(rejectionReasonKey(ret.rejectionReason))}</Fact>
              ) : null}
              {!ret.disposition && !ret.rejectionReason ? (
                <Fact label={t('status')}>
                  {ret.status === 'REQUESTED' ? t('returnNothingYet') : t(returnStatusKey(ret.status))}
                </Fact>
              ) : null}
            </Facts>
          </div>

          {/* The customer's own words, then the team's — kept apart, because only one of them is the customer's. */}
          <section className="flex flex-col gap-3">
            <div>
              <h3 className="mb-1.5 text-xs font-medium text-ink-muted">{t('customerNote')}</h3>
              {ret.customerNote ? (
                <p className="rounded-control border border-warning/40 bg-warning/5 px-3 py-2 text-sm whitespace-pre-line text-ink">
                  {ret.customerNote}
                </p>
              ) : (
                <p className="text-sm text-ink-faint">{t('returnNoCustomerNote')}</p>
              )}
            </div>
            {ret.staffNote ? (
              <div>
                <h3 className="mb-1.5 text-xs font-medium text-ink-muted">{t('staffNote')}</h3>
                <p className="rounded-control bg-surface-sunken px-3 py-2 text-sm whitespace-pre-line text-ink">
                  {ret.staffNote}
                </p>
              </div>
            ) : null}
          </section>

          <div className="grid gap-6 sm:grid-cols-2">
            <Facts title={t('returnMoneySection')}>
              <Fact label={t('returnWorth')} numeric>
                {money(ret.amount, locale)}
              </Fact>
              {ret.refundedAmount > 0 ? (
                <Fact label={t('refunded')} numeric>
                  <span className="text-success">{money(ret.refundedAmount, locale)}</span>
                </Fact>
              ) : null}
              {ret.refundedAt ? <Fact label={t('returnRefundedOn')}>{dateTime(ret.refundedAt, locale)}</Fact> : null}
              {linked ? (
                <Fact label={t('paymentMethod')}>
                  {linked.paymentMethod === 'COD' ? t('paymentCod') : t('paymentCard')}
                  {payment && payment.provider !== 'cod' ? (
                    <span dir="ltr" className="ms-1 text-xs text-ink-muted">
                      ({providerNames?.[payment.provider] ?? payment.provider})
                    </span>
                  ) : null}
                </Fact>
              ) : null}
              {payment?.providerPaymentId ? (
                <Fact label={t('paymentReference')}>
                  <span className="flex items-center gap-1">
                    <span dir="ltr" data-numeric className="truncate text-xs">
                      {payment.providerPaymentId}
                    </span>
                    <CopyButton value={payment.providerPaymentId} label={t('copy')} copiedLabel={t('copied')} />
                  </span>
                </Fact>
              ) : null}
            </Facts>

            <Facts title={t('returnOrderSection')}>
              <Fact label={t('orderNumber')}>
                <Link
                  to={`/orders/${ret.orderId}`}
                  dir="ltr"
                  data-numeric
                  className="font-medium text-brand-700 hover:underline"
                >
                  {ret.orderNumber}
                </Link>
              </Fact>
              {linked ? (
                <Fact label={t('customer')}>
                  {linked.customer?.id ? (
                    <Link to={`/customers/${linked.customer.id}`} className="text-brand-700 hover:underline">
                      {linked.customer.name || linked.shippingAddress.fullName}
                    </Link>
                  ) : (
                    <span className="inline-flex items-center gap-2">
                      {linked.shippingAddress.fullName}
                      <Pill>{t('guestCustomer')}</Pill>
                    </span>
                  )}
                  <span dir="ltr" className="block truncate text-xs text-ink-muted">
                    {linked.email}
                  </span>
                </Fact>
              ) : null}
            </Facts>
          </div>

          {mayReadOrders ? null : <p className="text-xs text-ink-muted">{t('returnPaymentHidden')}</p>}
        </div>
      )}
    </Sheet>
  );
}

/** A step's button. Refunding before the item is back says so — it is the exception. */
function stepLabel(step: ReturnStep, row: ReturnDto, t: ReturnType<typeof useT>): string {
  if (step === 'refund') return row.status === 'APPROVED' ? t('refundWithoutItem') : t('refund');
  return t(step);
}

function Facts({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section>
      <h3 className="mb-2 text-sm font-semibold text-ink">{title}</h3>
      <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-sm">{children}</dl>
    </section>
  );
}

function Fact({ label, numeric, children }: { label: string; numeric?: boolean; children: ReactNode }) {
  return (
    <>
      <dt className="text-ink-muted">{label}</dt>
      <dd data-numeric={numeric ? '' : undefined} className="min-w-0 text-ink [overflow-wrap:anywhere]">
        {children}
      </dd>
    </>
  );
}
